import { inject, injectable } from 'inversify';

import EpgService from '../../EpgService';
import { programIsFullyWatchable, programIsLive, programIsUpcoming } from '../../../utils/epg';
import { createURL } from '../../../utils/urlFormatting';
import type { EpgChannel, EpgProgram, EpgProgramNavigation } from '../../../../types/epg';
import type { PlaylistItem } from '../../../../types/playlist';

import type { EventItem, LineUpEventsResponse } from './types';
import { findImage } from './utils';
import StreamIQApiService from './StreamIQApiService';

@injectable()
export default class StreamIQEpgService extends EpgService {
  epgDays = 8;

  constructor(@inject(StreamIQApiService) protected readonly apiService: StreamIQApiService) {
    super();
  }

  transformProgram = (data: unknown) => {
    const eventItem = data as EventItem;

    const cardImage = findImage(eventItem.images, 'landscape', 'medium');

    return {
      id: `${eventItem.type}_${eventItem.id}`,
      navigable: true,
      channelId: `channel_${eventItem.channelId}`,
      title: eventItem.title,
      startTime: new Date(eventItem.startTime).toJSON(),
      endTime: new Date(eventItem.endTime).toJSON(),
      description: eventItem.description,
      cardImage,
      cardImageSmall: findImage(eventItem.images, 'landscape', 'small') || cardImage,
      backgroundImage: findImage(eventItem.images, 'landscape', 'large', 'backdrop'),
    };
  };

  fetchPrograms = async (items: PlaylistItem[], startTime?: number, endTime?: number) => {
    const [, lineupId] = items[0].feedid.split('_');

    const response = await this.apiService.get<LineUpEventsResponse>(
      createURL('/epg-service/events', {
        startTime,
        endTime,
        lineupId,
      }),
    );

    return response.resources.map(this.transformProgram);
  };

  /** Stream IQ events are backend resolvable, so every program is navigable. */
  getProgramNavigation = (program: EpgProgram, channel: EpgChannel): EpgProgramNavigation | undefined => {
    if (programIsUpcoming(program)) {
      return { type: 'media', programId: program.id };
    }

    if (!programIsFullyWatchable(program, channel.catchupHours)) {
      return programIsLive(program) ? { type: 'player' } : undefined;
    }

    if (programIsLive(program)) {
      return { type: 'player', programId: program.id };
    }

    return { type: 'player', programId: program.id, startAt: program.startTime, endAt: program.endTime };
  };
}
